import { css } from '@emotion/react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Process } from '../../components/Process';
import { formSteps, TFormStep } from './formSteps';
import { routes } from 'routes';
import { useStep } from './hooks/useStep';
import { ReactNode, useEffect } from 'react';
import { useQuery } from 'urql';
import { QUERY_BOOKER_INFO, QUERY_REQUEST } from 'queries/queries-booking';
import { format } from 'date-fns';
import { ConfirmedBox } from './components/ConfirmedBox';
import { theme } from 'styles/theme';
import { getFormattedPrice } from 'utils/utils';
import { FEE_TYPE } from './steps/FeeStep';
import { Badge } from 'components/Badge';
import { Tooltip, TooltipDirection } from 'components/Tooltip';
import { useEventDays } from './hooks/useEventDays';

export type BookingUrlParams = {
	id: string;
	stepId?: string;
	stepUrl?: string;
};

export type Step = {
	label: string;
	title: string;
	desc?: string;
	isRequired?: boolean;
};

export const BookingScreen = ({ children }: { children: ReactNode }) => {
	const { id, step, redirectNonexistStepToStart, redirectIfIsStepIsNotDone } = useStep();

	redirectNonexistStepToStart();

	//const history = useHistory();
	const navigate = useNavigate();

	const { pathname } = useLocation();
	// console.log(pathname);

	// Get information about organization
	const [bookerInfoResult] = useQuery({
		query: QUERY_BOOKER_INFO,
		variables: {
			url: id,
		},
	});

	// Get information about event
	const [requestResult] = useQuery({
		query: QUERY_REQUEST,
		variables: {
			url: id,
		},
	});

	const { data, fetching, error } = requestResult;

	useEffect(() => {
		if (!fetching && data) {
			// console.log(data);

			if (!data.request) {
				navigate(routes.booking.nonexisting);
			}

			redirectIfIsStepIsNotDone(data.request?.steps_done);
		}
	}, [data]);

	return (
		<div
			css={css`
				height: 100%;
			`}
		>
			<Navigation
				id={id}
				step={step}
				stepsDone={!fetching && data && data.request?.steps_done}
				request={data?.request}
				logo={bookerInfoResult?.data?.bookerByRequestUrl?.logo}
			/>

			{!fetching && data && <BookingStep request={data.request}>{children}</BookingStep>}
		</div>
	);
};

const BookingStep = ({ children, request }) => {
	return (
		<div
			css={css`
				display: flex;
				justify-content: center;
				padding-left: 280px;
				min-height: 100%;
			`}
		>
			<div
				css={css`
					width: 100%;
					justify-content: center;
					display: flex;
					padding-top: ${request?.confirmed ? 32 : 0}px;
					padding-bottom: 32px;
				`}
			>
				{request?.confirmed && <ConfirmedBox data={request} />}

				{children}
			</div>
		</div>
	);
};

export const Navigation = ({ id, step, request, logo }) => {
	const form: TFormStep[] = formSteps;

	const { restDaysString, isUrgent } = useEventDays({
		event_date: request?.event_date,
		isCompleted: request?.isCompleted,
	});

	return (
		<div
			css={css`
				width: 280px;
				background: #fafaff;
				position: fixed;
				left: 0;
				top: 0;
				bottom: 0;
				box-shadow: 1px 0 0 #f1f1f4;
				display: flex;
				flex-direction: column;
				justify-content: space-between;
				text-align: center;
			`}
		>
			<div
				css={css`
					background: #fafaff;
					display: flex;
					justify-content: start;
					align-items: center;
					padding: 20px 0;
					flex-direction: column;
					height: 33%;
				`}
			>
				{request && <img src={`data:image/svg+xml;base64,${btoa(logo)}`} />}

				<div
					css={css`
						margin: 20px;
						font-size: 13px;
						line-height: 20px;
						color: ${theme.color.text['300']};
					`}
				>
					{!!request && (
						<div>
							<div>
								<strong>{request.event_name}</strong>
							</div>
							<div>{request.artist?.name}</div>
							<div>{format(new Date(request.event_date), 'dd. MM. YYY')}</div>
							<div
								css={css`
									font-weight: bold;
									${!request.isCompleted &&
									isUrgent &&
									css`
										color: red;
									`}

									${request.isCompleted &&
									css`
										color: green;
									`}
								`}
							>
								{restDaysString}
							</div>

							<div
								css={css`
									margin-top: 4px;
								`}
							>
								{/* {request.fee_type}
								{getFormattedPrice(request.fee_amount + request.fee_travel, request.fee_vat)} */}

								{request.fee_type === FEE_TYPE.FIXED && (
									<Badge success={request.fee_vat} primary={!request.fee_vat}>
										{getFormattedPrice(request.fee_total, request.fee_vat)}
									</Badge>
								)}

								{request.fee_type === FEE_TYPE.OTHER && (
									<div
										css={css`
											display: inline-flex;
										`}
									>
										<Tooltip content={request.fee_other} direction={TooltipDirection.RIGHT}>
											<Badge secondary>Jiné podmínky</Badge>
										</Tooltip>
									</div>
								)}
							</div>

							<div></div>
						</div>
					)}
				</div>
			</div>
			<div>
				<Process
					id={id}
					steps={form}
					currentStep={step}
					stepsDone={request?.steps_done}
					confirmed={request?.confirmed}
					eventDate={request?.event_date}
				/>
			</div>
			<div
				css={css`
					color: ${theme.color.text['300']};
					font-size: 13px;
					padding: 20px;
					height: 33%;
					display: flex;
					flex-direction: column;
					justify-content: end;
				`}
			>
				© 2025 BookingHub
			</div>
		</div>
	);
};
